'use strict';

(() => {
  let cleanupNative;
  let scheduledInit;
  let productState;
  let cartState;
  let contactSuccessPublished = Boolean(history.state?.cnContactSuccessPublished);

  const initializeNative = () => {
    cleanupNative?.();
    const lifecycle = new AbortController();
    const cleanups = [];
    const timers = new Set();
    const frames = new Set();
    const requests = new Set();
    let disposed = false;
    const listen = (target, type, callback, options = {}) => target.addEventListener(type, callback, { ...options, signal: lifecycle.signal });
    const later = (callback, delay) => {
      const timer = setTimeout(() => { timers.delete(timer); if (!disposed) callback(); }, delay);
      timers.add(timer);
      return timer;
    };
    const frame = callback => {
      const id = requestAnimationFrame(() => { frames.delete(id); if (!disposed) callback(); });
      frames.add(id);
      return id;
    };
    cleanupNative = () => {
      if (disposed) return;
      disposed = true;
      lifecycle.abort();
      timers.forEach(clearTimeout);
      frames.forEach(cancelAnimationFrame);
      requests.forEach(request => request.abort());
      cleanups.forEach(cleanup => cleanup());
    };
  const publish = (name, data) => {
    if (!window.Shopify?.analytics?.publish || window.Shopify.designMode) return false;
    try {
      const result = window.Shopify.analytics.publish(`comfort_next:${name}`, data);
      if (result?.catch) result.catch(() => {});
      return result !== false;
    } catch (_) { return false; }
  };
  // Liquid cannot read arbitrary query parameters. Keep the explicit shopping
  // choice in links and GET forms, so it survives search, sort and pagination.
  const purchaseMode = value => value === 'buy' || value === 'rent' ? value : null;
  const query = new URLSearchParams(location.search);
  let journeyMode = purchaseMode(query.get('mode')) || purchaseMode(document.getElementById('cn-native-cart')?.dataset.cnPurchaseMode);
  let notifiedMode;
  const shoppingUrl = href => {
    try {
      const url = new URL(href, location.href);
      return url.origin === location.origin && /\/(?:products|collections)(?:\/|$)|\/search\/?$/.test(url.pathname) ? url : null;
    } catch (_) { return null; }
  };
  const hydrateJourney = () => {
    document.querySelectorAll('a[href]').forEach(link => {
      const url = shoppingUrl(link.getAttribute('href'));
      if (!url) return;
      const explicitMode = purchaseMode(link.dataset.cnPurchaseMode);
      // Existing variant links already identify the exact Buy/Rent item.
      if (url.searchParams.has('variant') && !explicitMode) return;
      const mode = explicitMode || journeyMode || (link.hasAttribute('data-cn-search-product') ? 'buy' : null);
      if (!mode) return;
      url.searchParams.set('mode', mode);
      link.href = url.pathname + url.search + url.hash;
    });
    document.querySelectorAll('form').forEach(form => {
      if ((form.getAttribute('method') || 'get').toLowerCase() !== 'get' || !shoppingUrl(form.getAttribute('action') || location.href)) return;
      let field = form.querySelector('input[name="mode"]');
      if (!journeyMode) return;
      if (!field) {
        field = document.createElement('input');
        field.type = 'hidden';
        field.name = 'mode';
        form.append(field);
      }
      field.value = journeyMode;
    });
    document.querySelectorAll('[data-cn-card-price]').forEach(price => {
      const rentalVisible = price.dataset.cnRentalVisible === 'true';
      const mode = journeyMode || purchaseMode(price.dataset.cnDefaultMode) || (rentalVisible ? 'rent' : 'buy');
      const showRent = rentalVisible && mode === 'rent';
      const primary = price.querySelector('.price-number');
      if (primary) primary.textContent = showRent ? price.dataset.cnRentPrice : price.dataset.cnBuyPrice;
      const period = price.querySelector('.cn-price-period');
      if (period) period.hidden = !showRent;
      const outright = price.querySelector('.cn-outright-price');
      if (outright) outright.hidden = !showRent;
      price.classList.toggle('cn-rental-price', showRent);
    });
    if (notifiedMode !== journeyMode) {
      notifiedMode = journeyMode;
      document.dispatchEvent(new CustomEvent('cn:purchase-mode:updated', { detail: { mode: journeyMode } }));
    }
  };
  hydrateJourney();
  listen(document, 'cn:catalogue:updated', hydrateJourney);
  listen(document, 'click', event => {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('a[href^="tel:"]');
    if (!link) return;
    publish('phone_click', {
      page_path: location.pathname,
      placement: link.closest('header, .site-header-stack') ? 'header' : link.closest('footer') ? 'footer' : 'content'
    });
  });
  const collectionSort = document.querySelector('.cn-collection-sort');
  if (collectionSort) listen(collectionSort, 'submit', () => {
    publish('collection_sort_applied', {
      collection: location.pathname.split('/').pop(),
      sort_by: collectionSort.querySelector('[name="sort_by"]').value
    });
  });
  const fitLinkTracking = document.getElementById('cn-fit-link');
  if (fitLinkTracking) listen(fitLinkTracking, 'click', () => {
    publish('fit_contact_clicked', { model: document.getElementById('cn-variant-select')?.selectedOptions[0]?.dataset.sku || '' });
  });
  const rentTab = document.getElementById('tab-rent');
  if (rentTab) listen(rentTab, 'click', () => publish('rent_tab_opened', {}));
  const rentContact = document.querySelector('#pane-rent .text-link');
  if (rentContact) listen(rentContact, 'click', () => publish('rent_catalogue_clicked', {}));
  const shopMenu = document.getElementById('cn-shop-menu');
  if (shopMenu) {
    listen(document, 'click', event => {
      if (!shopMenu.contains(event.target)) shopMenu.open = false;
    });
    listen(document, 'keydown', event => {
      if (event.key === 'Escape' && shopMenu.open) {
        shopMenu.open = false;
        shopMenu.querySelector('summary').focus();
      }
    });
  }
  // Product option pills drive the Shopify variant field.
  document.querySelectorAll('[data-cn-variant-choice]').forEach(choice => {
    listen(choice, 'change', () => {
      const target = document.getElementById('cn-variant-select');
      if (target && choice.checked) { target.value = choice.value; target.dispatchEvent(new Event('change', { bubbles: true })); }
    });
  });
  const productImage = document.getElementById('cn-product-image');
  const thumbnails = document.querySelectorAll('.native-thumbnail');
  const updateImage = (url, alt) => {
    if (!productImage || !url) return;
    productImage.removeAttribute('srcset');
    productImage.src = url;
    productImage.alt = alt || '';
    thumbnails.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.imageUrl === url)));
  };
  thumbnails.forEach(button => listen(button, 'click', () => updateImage(button.dataset.imageUrl, button.dataset.imageAlt)));
  const contactSuccess = document.querySelector('.native-contact-status--success');
  if (contactSuccess) {
    contactSuccess.focus();
    let attempts = 0;
    const publishContactSuccess = () => {
      if (contactSuccessPublished || disposed || window.Shopify?.designMode) return;
      contactSuccessPublished = publish('contact_form_success', { form_id: 'ContactForm', page_path: location.pathname });
      if (contactSuccessPublished) {
        try { history.replaceState({ ...history.state, cnContactSuccessPublished: true }, ''); } catch (_) {}
      } else if (++attempts < 20) later(publishContactSuccess, 250);
    };
    publishContactSuccess();
  }
  const contactBody = document.getElementById('ContactForm-body');
  const contactPreference = document.getElementById('ContactForm-preference');
  const contactPhone = document.getElementById('ContactForm-phone');
  const contactPhoneMarker = document.getElementById('ContactForm-phone-marker');
  if (contactPreference && contactPhone) {
    const updateContactPreference = () => {
      const callRequested = contactPreference.value === 'Phone call';
      contactPhone.required = callRequested;
      if (contactPhoneMarker) contactPhoneMarker.textContent = callRequested ? '*' : '(optional)';
    };
    listen(contactPreference, 'change', updateContactPreference);
    updateContactPreference();
  }
  const requestedModel = new URLSearchParams(location.search).get('model');
  if (contactBody && requestedModel && !contactBody.value.trim()) {
    contactBody.value = `Please check whether model ${requestedModel.slice(0, 80)} is a good fit for my home.`;
  }

  const variantSelect = document.getElementById('cn-variant-select');
  if (!document.getElementById('cn-buy-form')) document.querySelectorAll('.sticky-buy').forEach(bar => { bar.hidden = true; });
  if (variantSelect) {
    const buyForm = document.getElementById('cn-buy-form');
    // Visible model choices drive this submitted select once JavaScript runs.
    // Keep its no-script fallback, but avoid an invisible keyboard/AT control.
    const originalVariantTabIndex = variantSelect.getAttribute('tabindex');
    const originalVariantAriaHidden = variantSelect.getAttribute('aria-hidden');
    variantSelect.tabIndex = -1;
    variantSelect.setAttribute('aria-hidden', 'true');
    cleanups.push(() => {
      for (const [attribute, value] of [['tabindex', originalVariantTabIndex], ['aria-hidden', originalVariantAriaHidden]]) {
        if (value === null) variantSelect.removeAttribute(attribute);
        else variantSelect.setAttribute(attribute, value);
      }
    });
    const buyPrice = document.getElementById('cn-buy-price');
    const buyButton = document.getElementById('cn-buy-submit');
    const buyBox = document.getElementById('buy-box');
    const buyMode = document.getElementById('cn-buy-mode');
    const rentMode = document.getElementById('cn-rent-mode');
    const rentChoice = document.getElementById('cn-rent-choice');
    const rentPanel = document.getElementById('cn-rent-panel');
    const rentForm = document.getElementById('cn-rent-form');
    const rentId = document.getElementById('cn-rent-id');
    const rentMonthly = document.getElementById('cn-rent-monthly');
    const rentSummary = document.getElementById('cn-rent-summary');
    const rentBrief = document.getElementById('cn-rent-brief');
    const rentTerms = document.getElementById('cn-rent-terms');
    const rentTermsWrap = document.getElementById('cn-rent-terms-wrap');
    const rentPending = document.getElementById('cn-rent-pending');
    const stickyBuy = document.querySelector('.sticky-buy');
    const purchaseOnlySections = document.querySelectorAll('[data-cn-buy-only]');
    // Labels come from Theme settings: the rendered button text and data attributes.
    const buyLabel = buyButton.textContent;
    const unavailableLabel = buyButton.dataset.unavailableLabel ?? 'Currently unavailable';

    const modeStorageKey = `cn-purchase-mode:${location.pathname}`;
    const continuingProduct = productState?.key === modeStorageKey ? productState : null;
    // A Shopify-recognized variant is more specific than a journey choice.
    // Invalid variant parameters have no initial mode and never get rewritten
    // until the customer actively changes the selection.
    const requestedVariant = query.get('variant');
    const explicitOption = Array.from(variantSelect.options).find(option => option.value === requestedVariant || option.dataset.rentVariantId === requestedVariant);
    const explicitVariantMode = explicitOption ? (explicitOption.value === requestedVariant ? 'buy' : 'rent') : (!continuingProduct && requestedVariant ? purchaseMode(variantSelect.dataset.initialPurchaseMode) : null);
    if (!explicitVariantMode && continuingProduct?.variant && Array.from(variantSelect.options).some(option => option.value === continuingProduct.variant)) variantSelect.value = continuingProduct.variant;
    let preferredMode = explicitVariantMode || journeyMode || purchaseMode(continuingProduct?.mode);
    cleanups.push(() => {
      productState = { key: modeStorageKey, variant: variantSelect.value, mode: rentMode.checked && !rentChoice.hidden ? 'rent' : 'buy' };
    });
    const readSavedMode = () => {
      if (preferredMode) return preferredMode;
      try { return purchaseMode(sessionStorage.getItem(modeStorageKey)); } catch (_) { return null; }
    };
    const writeSavedMode = mode => {
      preferredMode = mode;
      try { sessionStorage.setItem(modeStorageKey, mode); } catch (_) {}
    };
    if (preferredMode) writeSavedMode(preferredMode);
    const syncVariantUrl = () => {
      const option = variantSelect.selectedOptions[0];
      if (!option) return;
      const url = new URL(location.href);
      const selectedId = rentMode.checked && !rentChoice.hidden && option.dataset.rentVariantId ? option.dataset.rentVariantId : option.value;
      url.searchParams.set('variant', selectedId);
      url.searchParams.set('mode', rentMode.checked && !rentChoice.hidden ? 'rent' : 'buy');
      history.replaceState(history.state, '', url);
    };
    const updateMode = () => {
      const showRent = rentMode.checked && !rentChoice.hidden;
      buyBox.hidden = showRent;
      rentPanel.hidden = !showRent;
      // The payment radios belong to this form. Its hidden default submitter
      // must never turn Enter on Rent into an outright purchase.
      buyButton.disabled = showRent || variantSelect.selectedOptions[0]?.dataset.buyReady !== 'true';
      const stickyButton = document.getElementById('cn-sticky-submit');
      if (stickyButton) stickyButton.disabled = buyButton.disabled;
      if (stickyBuy) stickyBuy.hidden = showRent;
      purchaseOnlySections.forEach(section => { section.hidden = showRent; });
      journeyMode = showRent ? 'rent' : 'buy';
      hydrateJourney();
    };
    const syncPurchaseMode = () => {
      const saved = readSavedMode();
      if (!rentChoice.hidden && saved !== 'buy') rentMode.checked = true;
      else buyMode.checked = true;
      updateMode();
    };
    const rememberMode = () => {
      writeSavedMode(rentMode.checked && !rentChoice.hidden ? 'rent' : 'buy');
      updateMode();
      syncVariantUrl();
    };

    const updateVariant = (syncUrl = false) => {
      const option = variantSelect.selectedOptions[0];
      if (!option) return;
      buyPrice.textContent = option.dataset.buyPrice ?? 'Price unavailable';
      buyButton.disabled = option.dataset.buyReady !== 'true';
      buyButton.textContent = buyButton.disabled ? unavailableLabel : buyLabel;
      document.querySelectorAll('[data-cn-variant-choice]').forEach(choice => { choice.checked = choice.value === option.value; });
      const stickyPrice = document.getElementById('cn-sticky-price');
      if (stickyPrice) stickyPrice.textContent = buyPrice.textContent;
      const stickyButton = document.getElementById('cn-sticky-submit');
      if (stickyButton) stickyButton.disabled = buyButton.disabled;
      const sku = document.getElementById('cn-product-sku');
      if (sku) {
        sku.textContent = option.dataset.sku ? `Model: ${option.dataset.sku}` : '';
        sku.hidden = !option.dataset.sku;
      }
      const factsModel = document.getElementById('cn-facts-model');
      if (factsModel) factsModel.textContent = option.dataset.sku || '';
      const fitLink = document.getElementById('cn-fit-link');
      if (fitLink) fitLink.href = `/pages/contact?model=${encodeURIComponent(option.dataset.sku || '')}`;
      updateImage(option.dataset.imageUrl, option.dataset.imageAlt);
      let approvedTermsUrl;
      try {
        approvedTermsUrl = new URL(option.dataset.rentTerms || '', location.href);
      } catch (error) {
        approvedTermsUrl = null;
      }
      const offerVisible = option.dataset.rentVisible === 'true' && Boolean(option.dataset.rentMonthly && option.dataset.rentInitial);
      const rentReady = offerVisible && option.dataset.rentReady === 'true' && Boolean(option.dataset.rentId && option.dataset.rentSummary?.trim() && option.dataset.rentTerms?.startsWith('https://') && approvedTermsUrl && approvedTermsUrl.protocol === 'https:');
      rentChoice.hidden = !offerVisible;
      if (!offerVisible) buyMode.checked = true;
      rentId.value = rentReady ? option.dataset.rentId : '';
      rentMonthly.textContent = offerVisible ? option.dataset.rentMonthly : '';
      rentSummary.textContent = rentReady ? option.dataset.rentSummary : '';
      rentSummary.hidden = !rentReady;
      rentBrief.hidden = !rentReady;
      rentTermsWrap.hidden = !rentReady;
      if (rentReady) {
        if (approvedTermsUrl.hostname === window.Shopify?.shop && location.protocol === 'https:') {
          approvedTermsUrl.host = location.host;
        }
        rentTerms.href = approvedTermsUrl.href;
      }
      else rentTerms.removeAttribute('href');
      rentForm.hidden = !rentReady;
      rentPending.hidden = rentReady;
      syncPurchaseMode();
      if (syncUrl) syncVariantUrl();
    };
    listen(variantSelect, 'change', () => updateVariant(true));
    listen(buyMode, 'change', rememberMode);
    listen(rentMode, 'change', rememberMode);
    listen(buyForm, 'submit', event => {
      if (!buyMode.checked || buyBox.hidden || variantSelect.selectedOptions[0]?.dataset.buyReady !== 'true') event.preventDefault();
    });
    listen(rentForm, 'submit', event => {
      if (rentForm.hidden || rentPanel.hidden || !rentMode.checked || rentChoice.hidden || !rentId.value || !rentTerms.href) event.preventDefault();
    });
    updateVariant();
    listen(window, 'pageshow', syncPurchaseMode);
    listen(window, 'load', () => {
      syncPurchaseMode();
      frame(syncPurchaseMode);
      later(syncPurchaseMode, 0);
      later(syncPurchaseMode, 100);
    });
  }

  const cartForm = document.getElementById('cn-native-cart');
  if (cartForm) {
    const date = document.getElementById('cn-install-date');
    const flexibleDate = document.getElementById('cn-flexible-date');
    const windowSelect = document.getElementById('cn-install-window');
    const status = document.getElementById('cn-preference-status');
    if (cartState) {
      date.value = cartState.date;
      if (Array.from(windowSelect.options).some(option => option.value === cartState.window)) windowSelect.value = cartState.window;
    }
    const originalWindowHidden = windowSelect.hidden;
    const originalWindowRequired = windowSelect.required;
    cleanups.push(() => { cartState = { date: date.value, window: windowSelect.value }; });
    const saveError = status.dataset.cnError ?? 'Could not save yet. Please try again before checkout.';
    const timeField = windowSelect.closest('.field');
    let timePicker;
    const torontoDate = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Toronto', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
    let today = torontoDate();
    date.min = today;
    const dateField = date.closest('.field');
    const dateTrigger = typeof window.flatpickr === 'function' ? document.createElement('button') : null;
    let datePickerKeyboard = false;
    if (dateTrigger) {
      dateTrigger.type = 'button';
      dateTrigger.id = 'cn-install-date-display';
      dateTrigger.className = 'cn-schedule-input cn-date-trigger';
      dateTrigger.setAttribute('aria-haspopup', 'dialog');
      dateTrigger.setAttribute('aria-controls', 'cn-install-calendar');
      dateTrigger.setAttribute('aria-expanded', 'false');
      const dateLabel = cartForm.querySelector('label[for="cn-install-date"]');
      if (dateLabel) {
        dateLabel.id = 'cn-install-date-label';
        dateLabel.htmlFor = dateTrigger.id;
        dateTrigger.setAttribute('aria-labelledby', `${dateLabel.id} ${dateTrigger.id}`);
      }
      date.parentElement.insertBefore(dateTrigger, date);
    }
    const syncDateTrigger = (_, __, picker) => {
      dateTrigger.textContent = picker.altInput.value || date.placeholder;
    };
    const datePicker = typeof window.flatpickr === 'function' ? window.flatpickr(date, {
      altInput: true,
      altFormat: 'M j',
      altInputClass: 'cn-schedule-input',
      ariaDateFormat: 'F j, Y',
      dateFormat: 'Y-m-d',
      minDate: today,
      disableMobile: true,
      clickOpens: false,
      ignoredFocusElements: [dateTrigger],
      positionElement: date.parentElement,
      onReady: syncDateTrigger,
      onValueUpdate: syncDateTrigger,
      onOpen: () => { refreshToday(); timePicker?.close(); dateTrigger.setAttribute('aria-expanded', 'true'); },
      onClose: () => {
        dateTrigger.setAttribute('aria-expanded', 'false');
        if (datePickerKeyboard) dateTrigger.focus({ preventScroll: true });
        datePickerKeyboard = false;
      }
    }) : null;
    function refreshToday() {
      const current = torontoDate();
      if (current !== today) {
        today = current;
        date.min = today;
        datePicker?.set('minDate', today);
        if (datePicker) syncDateTrigger([], '', datePicker);
      }
    }
    if (datePicker?.altInput) {
      // Flatpickr returns focus to this input after selection. Keep it hidden so
      // opening or selecting a date cannot focus a small text field on mobile.
      datePicker.altInput.type = 'hidden';
      listen(document, 'pointerdown', () => { datePickerKeyboard = false; }, { capture: true });
      const openDatePicker = keyboard => {
        datePickerKeyboard = keyboard;
        datePicker.open();
        if (keyboard && datePicker.isOpen) {
          const day = datePicker.calendarContainer.querySelector('.flatpickr-day.selected:not(.flatpickr-disabled)')
            ?? datePicker.calendarContainer.querySelector('.flatpickr-day.today:not(.flatpickr-disabled)')
            ?? datePicker.calendarContainer.querySelector('.flatpickr-day:not(.flatpickr-disabled):not(.hidden)');
          day?.focus({ preventScroll: true });
        }
      };
      listen(dateTrigger, 'click', event => {
        if (datePicker.isOpen) datePicker.close();
        else openDatePicker(event.detail === 0);
      });
      listen(dateTrigger, 'keydown', event => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); openDatePicker(true); }
        if (event.key === 'Escape') datePicker.close();
      });
      listen(datePicker.calendarContainer, 'keydown', event => {
        if (event.key === 'Tab' && event.shiftKey && event.target.classList.contains('flatpickr-day')) {
          event.preventDefault();
          event.stopImmediatePropagation();
          datePicker.close();
          dateTrigger.focus({ preventScroll: true });
        }
      }, { capture: true });
      const dateLabel = cartForm.querySelector('#cn-install-date-label');
      datePicker.calendarContainer.id = 'cn-install-calendar';
      datePicker.calendarContainer.setAttribute('role', 'dialog');
      datePicker.calendarContainer.setAttribute('aria-label', dateLabel?.textContent.trim() ?? 'Preferred date');
    }
    cleanups.push(() => {
      datePickerKeyboard = false;
      datePicker?.destroy();
      dateTrigger?.remove();
      const label = cartForm.querySelector('label[for="cn-install-date-display"]');
      if (label) label.htmlFor = 'cn-install-date';
    });
    const todayButton = datePicker ? document.createElement('button') : null;
    if (todayButton) {
      todayButton.type = 'button';
      todayButton.className = 'cn-calendar-today';
      todayButton.id = 'cn-calendar-today';
      todayButton.textContent = date.dataset.todayLabel ?? 'Today';
      listen(todayButton, 'click', () => { refreshToday(); datePicker.setDate(today, true); datePicker.close(); });
      datePicker.calendarContainer.appendChild(todayButton);
    }
    const timeTrigger = document.createElement('button');
    timeTrigger.type = 'button';
    timeTrigger.id = 'cn-install-time-toggle';
    timeTrigger.className = 'cn-schedule-input cn-time-trigger';
    timeTrigger.setAttribute('role', 'combobox');
    timeTrigger.setAttribute('aria-haspopup', 'listbox');
    timeTrigger.setAttribute('aria-expanded', 'false');
    timeTrigger.setAttribute('aria-required', 'true');
    timeTrigger.setAttribute('aria-controls', 'cn-install-time-menu');
    const timeLabel = cartForm.querySelector('label[for="cn-install-window"]');
    if (timeLabel) {
      timeLabel.id = 'cn-install-time-label';
      timeLabel.htmlFor = timeTrigger.id;
      timeTrigger.setAttribute('aria-labelledby', timeLabel.id);
    }
    const timeMenu = document.createElement('div');
    timeMenu.id = 'cn-install-time-menu';
    timeMenu.className = 'cn-time-dropdown';
    timeMenu.hidden = true;
    timeMenu.setAttribute('role', 'listbox');
    timeMenu.setAttribute('aria-labelledby', 'cn-install-time-label');
    const timeHeading = document.createElement('p');
    timeHeading.className = 'cn-time-menu-heading';
    timeHeading.textContent = timeLabel?.textContent.trim() ?? 'Preferred time';
    timeMenu.appendChild(timeHeading);
    const timeOptions = document.createElement('div');
    timeOptions.className = 'cn-time-options';
    timeOptions.setAttribute('role', 'presentation');
    timeMenu.appendChild(timeOptions);
    const timeItems = Array.from(windowSelect.options).filter(option => option.value).map((option, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.id = `cn-time-option-${index}`;
      button.className = 'cn-time-option';
      button.tabIndex = -1;
      button.setAttribute('role', 'option');
      button.dataset.timeValue = option.value;
      if (option.value === 'Flexible') button.classList.add('cn-time-option--flexible');
      button.textContent = option.textContent;
      listen(button, 'mousedown', event => event.preventDefault());
      listen(button, 'click', () => {
        if (button.disabled || timeTrigger.disabled) return;
        windowSelect.value = option.value;
        windowSelect.dispatchEvent(new Event('change', { bubbles: true }));
        closeTimeMenu();
        timeTrigger.focus();
      });
      timeOptions.appendChild(button);
      return { option, button };
    });
    let activeTime = -1;
    const setActiveTime = index => {
      activeTime = index;
      timeItems.forEach((item, itemIndex) => item.button.classList.toggle('is-active', index === itemIndex));
      if (index >= 0) {
        timeTrigger.setAttribute('aria-activedescendant', timeItems[index].button.id);
        timeItems[index].button.scrollIntoView?.({ block: 'nearest' });
      } else timeTrigger.removeAttribute('aria-activedescendant');
    };
    const closeTimeMenu = () => {
      timeMenu.hidden = true;
      timeTrigger.setAttribute('aria-expanded', 'false');
      timeTrigger.removeAttribute('aria-activedescendant');
    };
    const syncTimeMenu = () => {
      timeTrigger.textContent = windowSelect.selectedOptions[0]?.textContent ?? windowSelect.options[0].textContent;
      timeTrigger.disabled = windowSelect.disabled;
      timeItems.forEach(({ option, button }) => {
        button.disabled = option.disabled || windowSelect.disabled;
        button.setAttribute('aria-disabled', String(button.disabled));
        button.setAttribute('aria-selected', String(option.value === windowSelect.value));
      });
    };
    timePicker = { close: closeTimeMenu };
    windowSelect.parentElement.insertBefore(timeTrigger, windowSelect);
    document.body.appendChild(timeMenu);
    cleanups.push(() => {
      timeMenu.remove();
      timeTrigger.remove();
      windowSelect.hidden = originalWindowHidden;
      windowSelect.required = originalWindowRequired;
      if (timeLabel) timeLabel.htmlFor = 'cn-install-window';
    });
    windowSelect.hidden = true;
    windowSelect.required = false;
    const syncAvailableTimes = () => {
      refreshToday();
      const nowParts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Toronto', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
      }).formatToParts(new Date()).map(part => [part.type, part.value]));
      const currentMinutes = Number(nowParts.hour) * 60 + Number(nowParts.minute);
      Array.from(windowSelect.options).forEach(option => {
        if (option.dataset.minutes !== undefined) option.disabled = date.value === today && Number(option.dataset.minutes) <= currentMinutes;
      });
      if (windowSelect.selectedOptions[0]?.disabled) windowSelect.value = '';
      syncTimeMenu();
    };
    syncAvailableTimes();
    listen(window, 'focus', syncAvailableTimes);
    listen(document, 'visibilitychange', () => { if (!document.hidden) syncAvailableTimes(); });
    const openTimeMenu = () => {
      if (timeTrigger.disabled) return;
      syncAvailableTimes();
      datePicker?.close();
      timeMenu.hidden = false;
      timeTrigger.setAttribute('aria-expanded', 'true');
      const bounds = timeTrigger.getBoundingClientRect();
      const width = Math.min(312, window.innerWidth - 24);
      timeMenu.style.width = `${width}px`;
      const left = Math.max(12, Math.min(bounds.left, window.innerWidth - width - 12));
      let top = bounds.bottom + 8;
      if (window.innerHeight - bounds.bottom < timeMenu.offsetHeight + 20 && bounds.top > window.innerHeight - bounds.bottom) top = bounds.top - timeMenu.offsetHeight - 8;
      top = Math.max(12, Math.min(top, window.innerHeight - timeMenu.offsetHeight - 12));
      timeMenu.style.left = `${window.scrollX + left}px`;
      timeMenu.style.top = `${window.scrollY + top}px`;
      const selected = timeItems.findIndex(item => item.option.value === windowSelect.value && !item.button.disabled);
      setActiveTime(selected >= 0 ? selected : timeItems.findIndex(item => !item.button.disabled));
    };
    listen(timeTrigger, 'click', () => timeMenu.hidden ? openTimeMenu() : closeTimeMenu());
    listen(timeTrigger, 'keydown', event => {
      if (event.key === 'Escape' || event.key === 'Tab') { closeTimeMenu(); return; }
      if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter', ' '].includes(event.key)) return;
      event.preventDefault();
      if (timeMenu.hidden) { openTimeMenu(); return; }
      if (event.key === 'Enter' || event.key === ' ') { timeItems[activeTime]?.button.click(); return; }
      const enabled = timeItems.map((item, index) => item.button.disabled ? -1 : index).filter(index => index >= 0);
      if (!enabled.length) return;
      let index = enabled.indexOf(activeTime);
      if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = enabled.length - 1;
      else index = (index + (['ArrowUp', 'ArrowLeft'].includes(event.key) ? -1 : 1) + enabled.length) % enabled.length;
      setActiveTime(enabled[index]);
    });
    listen(document, 'mousedown', event => {
      if (!timeMenu.contains(event.target) && !timeTrigger.contains(event.target)) closeTimeMenu();
    });
    listen(window, 'resize', closeTimeMenu);
    listen(window, 'scroll', closeTimeMenu);
    listen(windowSelect, 'change', () => {
      syncTimeMenu();
      timeField?.classList.remove('invalid');
      timeTrigger.removeAttribute('aria-invalid');
    });
    const syncFlexible = () => {
      const flexible = windowSelect.value === 'Flexible';
      date.disabled = flexible;
      date.required = !flexible;
      flexibleDate.disabled = !flexible;
      if (datePicker?.altInput) datePicker.altInput.disabled = flexible;
      if (dateTrigger) dateTrigger.disabled = flexible;
      if (flexible) datePicker?.close();
      if (todayButton) todayButton.disabled = flexible;
      if (flexible) {
        dateField?.classList.remove('invalid');
        dateTrigger?.removeAttribute('aria-invalid');
      }
    };
    listen(windowSelect, 'change', syncFlexible);
    syncFlexible();
    let saveQueue = Promise.resolve();
    const savePreference = () => {
      dateField?.classList.remove('invalid');
      dateTrigger?.removeAttribute('aria-invalid');
      status.textContent = '';
      status.hidden = true;
      status.dataset.state = 'saving';
      const preferredDate = windowSelect.value === 'Flexible' ? 'Flexible' : date.value;
      const preferredWindow = windowSelect.value;
      saveQueue = saveQueue.catch(() => {}).then(async () => {
        if (disposed) return;
        const body = new FormData();
        body.append('attributes[Preferred installation date]', preferredDate);
        body.append('attributes[Preferred installation window]', preferredWindow);
        const controller = new AbortController();
        requests.add(controller);
        const timeout = later(() => controller.abort(), 8000);
        try {
          const response = await fetch(cartForm.dataset.updateUrl, {
            method: 'POST',
            body,
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
            signal: controller.signal
          });
          if (!response.ok) throw new Error('Cart update failed');
        } finally {
          clearTimeout(timeout);
          requests.delete(controller);
        }
      });
      saveQueue.then(() => {
        if (disposed) return;
        if ((preferredWindow === 'Flexible' || date.value === preferredDate) && windowSelect.value === preferredWindow) {
          status.dataset.state = 'saved';
          status.textContent = '';
          status.hidden = true;
          if (preferredWindow === 'Flexible') publish('cart_flexible_saved', {});
        }
      }).catch(() => {
        if (disposed) return;
        if ((preferredWindow === 'Flexible' || date.value === preferredDate) && windowSelect.value === preferredWindow) {
          status.dataset.state = 'error';
          status.textContent = saveError;
          status.hidden = false;
        }
      });
      return saveQueue;
    };
    listen(date, 'change', () => { syncAvailableTimes(); savePreference(); });
    listen(windowSelect, 'change', savePreference);
    const checkoutButton = cartForm.querySelector('[name="checkout"]');
    if (checkoutButton && typeof cartForm.requestSubmit === 'function') {
      let resumingCheckout = false;
      let checkoutPending = false;
      // Cart Ajax does not expose variant metafields. Compare the complete
      // rendered lines before using the eligibility that Liquid evaluated.
      // Any changed cart needs a fresh render of the Buy/Rent/terms gates.
      const cartSignature = cart => {
        if (!cart || !Array.isArray(cart.items) || !Number.isSafeInteger(cart.item_count) || !Number.isSafeInteger(cart.total_price) || cart.total_price < 0) throw new Error('Invalid cart');
        const lines = cart.items.map(item => {
          const variant = item.variant_id ?? item.id;
          if (typeof item.key !== 'string' || !item.key || !Number.isSafeInteger(variant) || variant <= 0 || !Number.isSafeInteger(item.quantity) || item.quantity <= 0 || !Number.isSafeInteger(item.final_line_price) || item.final_line_price < 0) throw new Error('Invalid cart line');
          return { key: item.key, variant_id: variant, quantity: item.quantity, final_line_price: item.final_line_price };
        }).sort((left, right) => left.key.localeCompare(right.key));
        if (new Set(lines.map(item => item.key)).size !== lines.length || lines.reduce((count, item) => count + item.quantity, 0) !== cart.item_count) throw new Error('Invalid cart count');
        return JSON.stringify({ item_count: cart.item_count, total_price: cart.total_price, items: lines });
      };
      const currentCartMatches = async () => {
        const rendered = cartSignature(JSON.parse(cartForm.querySelector('[data-cn-cart-snapshot]').textContent));
        const controller = new AbortController();
        requests.add(controller);
        const timeout = later(() => controller.abort(), 8000);
        try {
          const response = await fetch(cartForm.dataset.currentUrl, {
            credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal: controller.signal
          });
          if (!response.ok) throw new Error('Cart check failed');
          return cartSignature(await response.json()) === rendered;
        } finally {
          clearTimeout(timeout);
          requests.delete(controller);
        }
      };
      cleanups.push(() => {
        if (!checkoutPending) return;
        checkoutButton.disabled = false;
        date.readOnly = false;
        windowSelect.disabled = false;
      });
      const validSchedule = () => {
        syncAvailableTimes();
        const missingDate = windowSelect.value !== 'Flexible' && (!date.value || date.value < today);
        if (dateField) dateField.classList.toggle('invalid', missingDate);
        if (missingDate) {
          dateTrigger?.setAttribute('aria-invalid', 'true');
          (dateTrigger ?? date).focus({ preventScroll: true });
          datePicker?.open();
          return false;
        }
        const missingTime = !windowSelect.value || windowSelect.selectedOptions[0]?.disabled;
        timeField?.classList.toggle('invalid', missingTime);
        timeTrigger.setAttribute('aria-invalid', String(missingTime));
        if (missingTime) { timeTrigger.focus(); openTimeMenu(); return false; }
        return cartForm.reportValidity();
      };
      listen(cartForm, 'submit', async event => {
        if (resumingCheckout) return;
        event.preventDefault();
        if (checkoutPending || !validSchedule()) return;
        checkoutPending = true;
        checkoutButton.disabled = true;
        date.readOnly = true;
        if (dateTrigger) dateTrigger.disabled = true;
        windowSelect.disabled = true;
        syncTimeMenu();
        closeTimeMenu();
        datePicker?.close();
        if (datePicker?.calendarContainer) datePicker.calendarContainer.inert = true;
        if (todayButton) todayButton.disabled = true;
        try {
          await savePreference();
        } catch (error) {
          // The form post also carries both cart attributes if the Ajax save fails.
        }
        let cartMatches;
        try { cartMatches = await currentCartMatches(); } catch (error) { cartMatches = null; }
        if (disposed) return;
        checkoutButton.disabled = false;
        date.readOnly = false;
        windowSelect.disabled = false;
        syncTimeMenu();
        if (datePicker?.calendarContainer) datePicker.calendarContainer.inert = false;
        syncFlexible();
        checkoutPending = false;
        if (cartMatches === false) {
          location.assign(cartForm.action);
          return;
        }
        if (cartMatches === null) {
          status.dataset.state = 'error';
          status.textContent = status.dataset.cnCheckError ?? 'Could not check your cart. Please try again before checkout.';
          status.hidden = false;
          return;
        }
        // A midnight rollover or elapsed time slot can occur while saving.
        if (!validSchedule()) return;
        resumingCheckout = true;
        cartForm.requestSubmit(checkoutButton);
        resumingCheckout = false;
      });
    }
  }

  };
  const scheduleNative = () => {
    if (scheduledInit) cancelAnimationFrame(scheduledInit);
    scheduledInit = requestAnimationFrame(() => { scheduledInit = null; initializeNative(); });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeNative, { once: true });
  else initializeNative();
  document.addEventListener('shopify:section:load', scheduleNative);
  document.addEventListener('shopify:section:reorder', scheduleNative);
  document.addEventListener('shopify:section:unload', () => { cleanupNative?.(); scheduleNative(); });
})();
